import React, { useState, useEffect } from 'react'
import { Table, Input, DatePicker, Select, Tag, Button, Space, Modal, Avatar, Spin, Alert } from 'antd'
import { AppCard } from '@/shared/components/ui'
import { SearchOutlined, FilterOutlined, EyeOutlined } from '@ant-design/icons'
import { useI18n } from '@/shared/i18n'
import { useHistoryStore, Conversation } from './CustomerHistory.store'
import dayjs from 'dayjs'
import './CustomerHistory.scss'

const { RangePicker } = DatePicker
const { Option } = Select


const CustomerHistory: React.FC = () => {
  const { t } = useI18n('modules')
  const {
    filteredConversations,
    selectedConversation,
    isLoading,
    error,
    isDetailModalOpen,
    fetchConversations,
    fetchConversationDetail,
    setFilter,
    setSelectedConversation,
    setIsDetailModalOpen,
    applyFilters,
    resetFilters
  } = useHistoryStore()
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState<string>('time')
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null)

  // 加载数据
  useEffect(() => {
    fetchConversations()
  }, [fetchConversations])

  const handleSearch = (value: string) => {
    setSearchText(value)
    setFilter({ searchText: value })
  }

  const handleViewDetail = (conversationId: string) => {
    fetchConversationDetail(conversationId)
    // modal会在store中通过isDetailModalOpen状态控制
  }



  const columns = [
    {
      title: t('history.table.columns.customer'),
      dataIndex: 'customer',
      key: 'customer',
      width: 100,
      render: (text: string, record: Conversation) => (
        <div>
          <div>{text}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>{record.customerId}</div>
        </div>
      ),
    },
    {
      title: t('history.table.columns.time'),
      dataIndex: 'startTime',
      key: 'startTime',
      width: 150,
      render: (text: string) => dayjs(text).format('MM-DD HH:mm'),
    },
    {
      title: t('history.table.columns.duration'),
      dataIndex: 'duration',
      key: 'duration',
      width: 80,
    },
    {
      title: t('history.table.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const statusConfig = {
          completed: { color: 'success', text: t('history.status.completed') },
          ongoing: { color: 'processing', text: t('history.status.ongoing') },
          cancelled: { color: 'error', text: t('history.status.cancelled') },
        }
        const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status }
        return <Tag color={config.color}>{config.text}</Tag>
      },
    },
    {
      title: t('history.table.columns.satisfaction'),
      dataIndex: 'satisfaction',
      key: 'satisfaction',
      width: 100,
      render: (score: number | undefined) =>
        score ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {'⭐'.repeat(score)}
            <span style={{ marginLeft: 4 }}>{t('history.satisfaction.stars', { score })}</span>
          </div>
        ) : (
          t('history.satisfaction.notEvaluated')
        ),
    },
    {
      title: t('history.table.columns.tags'),
      dataIndex: 'tags',
      key: 'tags',
      width: 150,
      render: (tags: string[]) => (
        <Space size="small" wrap>
          {tags.map(tag => (
            <Tag key={tag} color="default" style={{ fontSize: '12px' }}>
              {tag}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: t('history.table.columns.actions'),
      key: 'actions',
      width: 100,
      render: (_: any, record: Conversation) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record.id)}
        >
          {t('history.buttons.view')}
        </Button>
      ),
    },
  ]

  return (
    <div className="customer-history">
      <div className="history-header">
        <AppCard>
          <div className="history-filters">
            <Space.Compact className="search-filter">
              <Input
                placeholder={t('history.search.placeholder')}
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={() => handleSearch(searchText)}
                className="search-filter-field"
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={() => handleSearch(searchText)}
              />
            </Space.Compact>
            <Space wrap>
              <RangePicker
                onChange={(dates) => {
                  setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])
                  setFilter({ dateRange: dates ? dates as [dayjs.Dayjs, dayjs.Dayjs] : undefined })
                }}
                placeholder={[t('history.filters.dateRange.startDate'), t('history.filters.dateRange.endDate')]}
              />
              <Select
                placeholder={t('history.filters.status')}
                value={statusFilter}
                onChange={(value) => {
                  setStatusFilter(value)
                  setFilter({ status: value })
                }}
                style={{ width: 120 }}
              >
                <Option value="all">{t('history.filters.allStatus')}</Option>
                <Option value="completed">{t('history.status.completed')}</Option>
                <Option value="ongoing">{t('history.status.ongoing')}</Option>
                <Option value="cancelled">{t('history.status.cancelled')}</Option>
              </Select>
              <Select
                value={sortBy}
                onChange={(value) => {
                  setSortBy(value)
                  setFilter({ sortBy: value })
                }}
                style={{ width: 140 }}
              >
                <Option value="time">{t('history.sort.byTime')}</Option>
                <Option value="satisfaction">{t('history.sort.bySatisfaction')}</Option>
              </Select>
              <Button
                icon={<FilterOutlined />}
                onClick={() => {
                  setSearchText('')
                  setStatusFilter('all')
                  setSortBy('time')
                  setDateRange(null)
                  resetFilters()
                }}
              >
                {t('history.buttons.reset')}
              </Button>
            </Space>
          </div>
        </AppCard>
      </div>

      {error && (
        <Alert
          message={t('history.messages.error')}
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => {/* 可以添加清除错误的action */}}
          style={{ marginBottom: 16 }}
        />
      )}

      <AppCard className="history-content">
        <Table
          dataSource={filteredConversations}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          size="middle"
          loading={isLoading}
          locale={{ emptyText: isLoading ? t('history.table.loadingText') : t('history.table.emptyText') }}
          scroll={{ x: 'max-content' }}
        />
      </AppCard>

      <Modal
        title={t('history.modals.detailTitle')}
        open={isDetailModalOpen}
        onCancel={() => setIsDetailModalOpen(false)}
        width={800}
        footer={null}
        destroyOnHidden
        centered
        styles={{ body: { maxHeight: 'calc(100vh - 160px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
      >
        {selectedConversation && (
          <div className="conversation-detail">
            <div className="detail-header">
              <div className="customer-info-grid">
                <div className="info-item">
                  <span className="info-label">{t('history.modals.customerName')}</span>
                  <span className="info-value">{selectedConversation.customerInfo.name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t('history.modals.customerEmail')}</span>
                  <span className="info-value">{selectedConversation.customerInfo.email || t('history.modals.noContactInfo')}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t('history.modals.customerPhone')}</span>
                  <span className="info-value">{selectedConversation.customerInfo.phone || t('history.modals.noContactInfo')}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t('history.modals.customerLocation')}</span>
                  <span className="info-value">{selectedConversation.customerInfo.location || t('history.modals.unknownLocation')}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t('history.modals.customerSegment')}</span>
                  <span className="info-value">{selectedConversation.customerInfo.segment || t('history.modals.regularUser')}</span>
                </div>
              </div>
            </div>

            <div className="detail-content">
              <h3>{t('history.modals.conversationContent')}</h3>
              {selectedConversation.messages.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                  {t('history.modals.noConversationContent')}
                </div>
              ) : (
                <div className="chat-messages">
                  {selectedConversation.messages.map(message => (
                    <div key={message.id} className={`chat-message ${message.sender === 'customer' ? 'chat-message-left' : 'chat-message-right'}`}>
                      <Avatar size="small" className="chat-avatar">
                        {message.sender === 'customer' ? t('history.modals.customer')[0] : t('history.modals.agent')[0]}
                      </Avatar>
                      <div className="chat-bubble-wrapper">
                        <div className="chat-meta">
                          <span className="chat-sender-name">
                            {message.sender === 'customer' ? t('history.modals.customer') : t('history.modals.agent')}
                          </span>
                          <span className="chat-time">{message.timestamp}</span>
                        </div>
                        <div className={`chat-bubble ${message.sender === 'customer' ? 'chat-bubble-customer' : 'chat-bubble-agent'}`}>
                          {message.content}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        )}
      </Modal>
    </div>
  )
}

export default CustomerHistory